import {
    AppBorderBox,
    AppButton,
    AppModal,
    AppPaginator,
    AppPaper,
    AppSkeletonGlobalCardList,
    AppText,
    AppTitle,
} from "../../../commons/components";
import AppBox from "../../../commons/components/Box";
import {useAppDispatch, useAppSelector} from "../../../hooks/app";
import {StatusEnum} from "../../../commons/enums/status-enum";
import {Can} from "../../../casl/Can";
import {RolePolicyActionEnum} from "../../../commons/enums/role-policy-action.enum";
import SubTeamCard from "./components/teamSubTeam/SubTeamCard";
import IconAdd from "../../../commons/components/icons/add";
import {useEffect, useState} from "react";
import IconBranch from "../../../commons/components/icons/branch";
import IconBin from "../../../commons/components/icons/bin";
import SubTeamDelete from "./components/teamSubTeam/SubTeamDelete";
import SubTeamDeleteInprogress from "./components/teamSubTeam/SubTeamDeleteInprogress";
import SubTeamAddEdit from "./components/teamSubTeam/teamAddEdit/SubTeamAddEdit";
import {fetchManageSubTeamList} from "../../../redux/slices/football/admin/team/sub-team/manage-sub-team-list-slice";
import {ManageFootballSubTeam} from "../../../commons/models/football/admin/manage-football-sub-team";
import {TeamStatusEnum} from "../../../commons/enums/team-status-enum";
import IconProhibited from "../../../commons/components/icons/prohibited";
import SubTeamTerminate from "./components/teamSubTeam/SubTeamTerminate";
import IconCheckMarkCircle from "../../../commons/components/icons/checkmark-circle";
import SubTeamActivate from "./components/teamSubTeam/SubTeamActivate";
import {subTeamTerminate} from "../../../redux/slices/football/admin/team/sub-team/manage-sub-team-terminate-slice";
import {useSnackbar} from "../../../redux/snackbarProvider";
import {AxiosError} from "axios";
import {IServerErrorResponse} from "../../../commons/components/interface";
import {subTeamActivate} from "../../../redux/slices/football/admin/team/sub-team/manage-sub-team-activate-slice";
import {cleanUpStepperFormData} from "../../../redux/slices/stepper-form-slice";
import {subTeamDelete} from "../../../redux/slices/football/admin/team/sub-team/manage-sub-team-delete-slice";
import usePagination from "../../../hooks/usePagination";
import FilterToolbar from "../../../commons/components/ui-components/FilterToolbar";
import useFilter from "../../../hooks/useFilter";

export default function TeamManageSubTeams() {
    const dispatch = useAppDispatch();
    const addSnackbar = useSnackbar();
    const {teamInfo} = useAppSelector((state) => state.footballManageTeam);
    const {subTeamList, subTeamListStatus} = useAppSelector((state) => state.footballManageSubTeamList);
    const {subTeamDeleteStatus} = useAppSelector((state) => state.footballManageSubTeamDelete);
    const {subTeamTerminateStatus} = useAppSelector((state) => state.footballManageSubTeamTerminate);
    const {subTeamActivateStatus} = useAppSelector((state) => state.footballManageSubTeamActivate);
    const [selectedSubTeam, setSelectedSubTeam] = useState<ManageFootballSubTeam | null>(null);
    const [isSubteamAddEdit, setIsSubteamAddEdit] = useState<boolean>();
    const [isSubteamDelete, setIsSubteamDelete] = useState<boolean>();
    const [isTerminateSubTeam, setTerminateSubTeam] = useState(false);
    const [isActivateSubTeam, setActivateSubTeam] = useState(false);
    const {page, perPage, pageLimitChange, pageChange} = usePagination();
    const {status, sortOrder, sortBy, searchText,} = useFilter();

    useEffect(() => {
        if (teamInfo.id) {
            dispatch(
                fetchManageSubTeamList({
                    teamId: teamInfo.id,
                    urlParams: {
                        sort: sortBy,
                        sortOrder,
                        perPage,
                        page,
                        status,
                        name: searchText
                    }
                })
            );
        }
    }, [teamInfo, page, perPage, status, sortOrder, sortBy, searchText]);

    const reFetchSubTeams = () => {
        dispatch(
            fetchManageSubTeamList({
                teamId: teamInfo.id,
                urlParams: {
                    sort: sortBy,
                    sortOrder,
                    status,
                    name: searchText
                }
            })
        );
    }

    const handleStatusChange = (value: string | number, team: ManageFootballSubTeam) => {
        if (value === TeamStatusEnum.Terminated.toString()) {
            setSelectedSubTeam(team);
            setTerminateSubTeam(true);
        } else {
            setSelectedSubTeam(team);
            setActivateSubTeam(true);
        }
    };

    const handleTerminateConfirm = (e: Event) => {
        e.preventDefault();
        if (selectedSubTeam) {
            dispatch(subTeamTerminate({teamId: teamInfo.id, subTeamId: selectedSubTeam.id}))
                .unwrap()
                .then(() => {
                    addSnackbar({
                        key: "sub-team-terminate-success",
                        text: "Sub-team Terminated Successfully",
                        variant: "success",
                    });
                    setSelectedSubTeam(null);
                    reFetchSubTeams();
                })
                .catch((error: AxiosError<IServerErrorResponse>) => {
                    addSnackbar({
                        key: "error",
                        text: error.response?.data.message,
                        variant: "danger",
                    });
                });
            setTerminateSubTeam(false);
        }
    };

    const handleActivateConfirm = (e: Event) => {
        e.preventDefault();
        if (selectedSubTeam) {
            dispatch(subTeamActivate({teamId: teamInfo.id, subTeamId: selectedSubTeam.id}))
                .unwrap()
                .then(() => {
                    addSnackbar({
                        key: "team-active-success",
                        text: "Team Activated Successfully",
                        variant: "success",
                    });
                    setSelectedSubTeam(null);
                    reFetchSubTeams()
                })
                .catch((error: AxiosError<IServerErrorResponse>) => {
                    addSnackbar({
                        key: "error",
                        text: error.response?.data.message,
                        variant: "danger",
                    });
                });
            setActivateSubTeam(false);
        }
    };

    const handleAfterAddEdit = () => {
        setIsSubteamAddEdit(false);
        setSelectedSubTeam(null);
        reFetchSubTeams();
    };

    const handleAfterAddEditClose = () => {
        setIsSubteamAddEdit(false);
        setSelectedSubTeam(null);
        dispatch(cleanUpStepperFormData());
    };

    const handleDeleteConfirm = (e: Event) => {
        e.preventDefault();
        if (selectedSubTeam) {
            setIsSubteamDelete(false);
            dispatch(subTeamDelete({teamId: teamInfo.id, subTeamId: selectedSubTeam.id}))
                .unwrap()
                .then(() => {
                    addSnackbar({
                        key: "subteam-delete-success",
                        text: "Sub-team Deleted Successfully",
                        variant: "success",
                    });
                    setSelectedSubTeam(null);
                    reFetchSubTeams();
                })
                .catch((error: AxiosError<IServerErrorResponse>) => {
                    addSnackbar({
                        key: "error",
                        text: error.response?.data.message,
                        variant: "danger",
                    });
                });
        }
    };

    return (
        <>
            {subTeamListStatus === StatusEnum.Loading ? (
                <AppSkeletonGlobalCardList withAvatar withText numberOfItems={5}/>
            ) : (
                <AppBox mb="sm" gap="sm" flexDirection="column">
                    <AppBox alignItems="center" justifyContent="space-between">
                        <AppTitle as="h5">Sub-teams</AppTitle>
                        <Can I={RolePolicyActionEnum.UPDATE} this={teamInfo}>
                            {subTeamList.all().length > 0 && (
                                <AppButton label="Add Sub-Team" variant="light"
                                           onClick={() => setIsSubteamAddEdit(true)}/>
                            )}
                        </Can>
                    </AppBox>
                    <FilterToolbar sortByOptions={[
                        {
                            title: "Name",
                            value: 'name',
                        },
                        {
                            title: "Short Name",
                            value: "shortName",
                        },
                        {
                            title: "Founded Date",
                            value: "founded",
                        },
                        {
                            title: "Created Date",
                            value: "createdAt",
                        }
                    ]} statusOptions={[
                        {
                            title: "All",
                            value: '',
                        },
                        {
                            title: "Active",
                            value: TeamStatusEnum.Active,
                        },
                        {
                            title: "Terminated",
                            value: TeamStatusEnum.Terminated,
                        },
                    ]}/>
                    {subTeamList.all().length === 0 ? (
                        <AppPaper padding="sm">
                            <AppBorderBox borderVariant="dashed">
                                <AppBox flexDirection="column" alignItems="center" gap="sm" justifyContent="center"
                                        p="sm">
                                    <AppBox alignItems="center" flexDirection="column" justifyContent="center" gap="xs">
                                        <AppBorderBox radius="rounded" p="2xs">
                                            <IconAdd width={40} height={40} color="rgba(var(--border-200))"/>
                                        </AppBorderBox>
                                        <AppText fontWeight="semibold">
                                            There are not any sub-teams in the team.{" "}
                                            <Can I={RolePolicyActionEnum.UPDATE} this={teamInfo}>
                                                To add sub-team, click 'Add Sub-team'.
                                            </Can>
                                        </AppText>
                                    </AppBox>
                                    <Can I={RolePolicyActionEnum.UPDATE} this={teamInfo}>
                                        <AppButton label="Add Sub-team" onClick={() => setIsSubteamAddEdit(true)}/>
                                    </Can>
                                </AppBox>
                            </AppBorderBox>
                        </AppPaper>
                    ) : (
                        <AppBox flexDirection="column" gap="md">
                            <AppBox flexDirection="column" gap="sm">
                                {subTeamList.all().map((subTeam, index) => (
                                    <SubTeamCard
                                        key={index}
                                        teamInfo={teamInfo}
                                        subTeamInfo={subTeam}
                                        onEdit={() => {
                                            setSelectedSubTeam(subTeam);
                                            setIsSubteamAddEdit(true);
                                        }}
                                        onStatusChange={(value) => {
                                            handleStatusChange(value, subTeam);
                                        }}
                                        onDelete={() => {
                                            setSelectedSubTeam(subTeam);
                                            setIsSubteamDelete(true);
                                        }}
                                    />
                                ))}
                            </AppBox>
                            {subTeamList?.pageCount > 1 &&
                                <AppPaginator
                                    perPage={subTeamList.perPage}
                                    pageCount={subTeamList.pageCount}
                                    currentPage={subTeamList.page}
                                    pageChange={pageChange}
                                    perPageLimitChange={pageLimitChange}/>
                            }
                        </AppBox>
                    )}
                </AppBox>
            )}
            <AppModal
                title={selectedSubTeam ? "Edit Sub-team" : "Add A Sub-team"}
                modalSize="lg"
                titleIcon={<IconBranch/>}
                opened={isSubteamAddEdit}
                onClose={() => {
                    handleAfterAddEditClose();
                }}
                withoutButtons
            >
                <SubTeamAddEdit
                    teamInfo={teamInfo}
                    subTeamData={selectedSubTeam}
                    onSave={() => {
                        handleAfterAddEdit();
                    }}
                />
            </AppModal>
            <AppModal
                opened={isSubteamDelete}
                onClose={() => {
                    setIsSubteamDelete(false);
                    setSelectedSubTeam(null);
                }}
                title="Want To Remove This Sub-team?"
                titleIcon={<IconBin/>}
                iconColor="danger"
                confirmButtonLabel="Remove"
                onSave={(e) => {
                    handleDeleteConfirm(e as Event);
                }}
            >
                <SubTeamDelete subTeam={selectedSubTeam!}/>
            </AppModal>
            <AppModal
                opened={subTeamDeleteStatus === StatusEnum.Loading}
                title="Removing Team"
                titleIcon={<IconBin/>}
                iconColor="danger"
                withoutCancelButton
                withoutButtons
                withoutCloseButton
            >
                <SubTeamDeleteInprogress subTeam={selectedSubTeam!}/>
            </AppModal>
            <AppModal
                opened={isTerminateSubTeam}
                title="Want To Terminate This Sub-team?"
                titleIcon={<IconProhibited/>}
                iconColor="danger"
                confirmButtonLabel="Terminate"
                withLoadingButton={subTeamTerminateStatus === StatusEnum.Loading}
                onClose={() => {
                    setTerminateSubTeam(false);
                }}
                onSave={(e) => {
                    handleTerminateConfirm(e as Event);
                }}
            >
                <SubTeamTerminate subTeam={selectedSubTeam!}/>
            </AppModal>
            <AppModal
                opened={isActivateSubTeam}
                title="Want To Activate This Sub-team?"
                titleIcon={<IconCheckMarkCircle/>}
                iconColor="success"
                confirmButtonLabel="Activate"
                withLoadingButton={subTeamActivateStatus === StatusEnum.Loading}
                onClose={() => {
                    setActivateSubTeam(false);
                }}
                onSave={(e) => {
                    handleActivateConfirm(e as Event);
                }}
            >
                <SubTeamActivate subTeam={selectedSubTeam!}/>
            </AppModal>
        </>
    );
}
